import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { findCategory } from "../../../libs/category";
import useErrors from "../../../libs/useErrors";
import { getWritable, replaceTabName } from "../../../libs/talk";

import { KeyValue } from "../../../types/keyValue";
import {
  CATEGORY,
  CATEGORY_ALIAS,
  SANDI_TALK_BLOCK_COMPANY_LIST,
} from "../../../types/talk";

import { useSandiTalk } from "../../../contexts/boardContext";
import {
  addNewPost,
  AddNewPostRequest,
  TalkCategory,
  TalkDetail,
  updatePost,
} from "../../../services/talkServices";
import { useUserContext } from "../../../contexts/userContext";

import useFullScreenLoading from "../../../hooks/useFullScreenLoading";

import CommonButton from "../../Button";
import CommonInput from "../../Input/CommonInput";
import CommonImageInput from "../../Input/ImageUpload";
import RadioButtonGroup from "../../Input/RadioButtonGroup";
import StickyHeader from "../../Layout/StickyHeader";
import HorizontalRule from "../../Rule/Horizontal";
import CommonSelect, { SelectOption } from "../../Select";
import CommonTextArea from "../../TextArea";
import SimplePopup from "../../Popup/Simple";
import CommonLabel from "../../Label";

type Props = { post?: TalkDetail };

export default function TalkModifyPost({ post }: Props) {
  const navigate = useNavigate();
  const [talk, { updateCategory }] = useSandiTalk();
  const [user] = useUserContext();
  const titleRef = React.useRef<HTMLInputElement>(null);
  const contentRef = React.useRef<HTMLTextAreaElement>(null);
  const [errors, { removeError, setError }] = useErrors();

  const [postInfo, setPostInfo] = React.useState<Partial<AddNewPostRequest>>(
    post ?? {
      useComment: true,
    }
  );
  const [images, setImages] = useState<(File | string)[]>([]);
  const [popup, setPopup] = useState<
    "completeUpdate" | "completeInsert" | "error" | ""
  >("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const loadingComponent = useFullScreenLoading({ isLoading });

  const useCommentOptions = [
    { key: "true", value: "허용" },
    { key: "false", value: "비허용" },
  ];

  const checkSandiTalkBlockedUser = () => {
    const userCompanyId = user.companyId;
    if (userCompanyId) {
      return SANDI_TALK_BLOCK_COMPANY_LIST.includes(userCompanyId);
    } else {
      return false; // 식별 할 수 없을 땐 샌디톡 활성화
    }
  };

  const radioList =
    talk.categoryList
      ?.filter((item) => item.alias !== CATEGORY_ALIAS.SANDI_INFO)
      .map(
        (item) =>
          ({
            key: item.alias,
            value: replaceTabName(item.name ?? ""),
            disabled: checkSandiTalkBlockedUser() && item.alias === "talk",
          } as KeyValue)
      ) ?? [];

  const filterUnusedCategory = (category: TalkCategory) => {
    if (
      category.lifeCategoryId === CATEGORY.ALL || // 전체글 카테고리 필터링
      category.lifeCategoryId === CATEGORY.POPULAR ||
      category.lifeCategoryId === CATEGORY.BUILDING_ALL
    ) {
      return false;
    }

    // 메뉴 순서가 바뀌면서 자동 generated된 카테고리가 id로 필터링에 실패함
    // alias 기준으로 한번 더 필터링하도록 설정
    if (
      category.alias === CATEGORY_ALIAS.TALK_ALL ||
      category.alias === CATEGORY_ALIAS.TALK_POPULAR ||
      category.alias === CATEGORY_ALIAS.BUILDING_ALL
    ) {
      return false;
    }

    return getWritable(category, user);
  };

  // TODO: 상위에서 post 데이터 내려 올때 기존 category 찾아서 집어넣는 문제 개선 필요
  const [selectedMiddleCategory, setSelectedMiddleCategory] = React.useState<
    TalkCategory | undefined
  >(
    talk?.largeCategory?.lifeCategoryList
      ?.filter(filterUnusedCategory)
      .find(
        (category) =>
          category.lifeCategoryId === talk.middleCategory?.lifeCategoryId
      ) ??
      talk?.largeCategory?.lifeCategoryList?.filter(filterUnusedCategory)[0]
  );

  const getOptionFromCategory = (category?: TalkCategory) => {
    if (!category) return {} as SelectOption;

    if (category.lifeCategoryId === CATEGORY.ALL) {
    }

    return {
      label: category.name,
      value: category.lifeCategoryId?.toString(),
    } as SelectOption;
  };

  const handleInsert = async () => {
    const data = new FormData();

    data.append("title", postInfo.title as string);
    data.append(
      "lifeCategoryId",
      selectedMiddleCategory?.lifeCategoryId?.toString() ?? ""
    );
    const doc = new DOMParser().parseFromString(
      postInfo.content as string,
      "text/html"
    );
    data.append("content", (doc.body.textContent || "") as string);
    data.append("status", "PUBLIC");

    if (postInfo.useComment !== null && postInfo.useComment !== undefined) {
      data.append("useComment", postInfo.useComment?.toString() ?? "");
    }
    if (
      selectedMiddleCategory?.alias?.startsWith(CATEGORY_ALIAS.BUILDING) &&
      talk?.buildingId
    ) {
      data.append("buildingId", talk.buildingId);
    }
    if (
      selectedMiddleCategory?.alias === CATEGORY_ALIAS.BUILDING_MENU &&
      talk.storeId
    ) {
      data.append("storeId", talk.storeId.toString());
    }
    images.forEach((image) => data.append("imageFiles", image));

    await addNewPost(data);
  };

  const handleUpdate = async () => {
    const data = new FormData();
    data.append("title", postInfo.title as string);
    const doc = new DOMParser().parseFromString(
      postInfo.content as string,
      "text/html"
    );
    data.append("content", (doc.body.textContent || "") as string);
    data.append(
      "lifeCategoryId",
      selectedMiddleCategory?.lifeCategoryId?.toString() ?? ""
    );
    data.append("status", "PUBLIC");

    if (postInfo.useComment !== null && postInfo.useComment !== undefined) {
      data.append("useComment", postInfo.useComment?.toString() ?? "");
    }
    if (
      selectedMiddleCategory?.alias?.startsWith(CATEGORY_ALIAS.BUILDING) &&
      talk?.buildingId
    ) {
      data.append("buildingId", talk.buildingId);
    }
    if (
      selectedMiddleCategory?.alias === CATEGORY_ALIAS.BUILDING_MENU &&
      talk.storeId
    ) {
      data.append("storeId", talk.storeId.toString());
    }

    let beforeList = post?.fileInfo.items.filter(
      (item) => item.scale === "full"
    );
    images.forEach((image) => {
      if (typeof image !== "string") {
        data.append("imageFiles", image);
      } else {
        beforeList = beforeList?.filter((item) => item.url !== image);
      }
    });
    beforeList?.map((item) =>
      data.append("deleteFileSortOrders", item.sortOrder.toString())
    );

    return updatePost(post?.lifePostId as number, data);
  };

  const validate = (dryrun: boolean = false) => {
    if (!postInfo?.title) {
      if (dryrun === false) {
        titleRef.current?.focus();
        setError("title", "제목을 입력해주세요.");
      }
      return false;
    }

    if (!postInfo?.content) {
      if (dryrun === false) {
        contentRef.current?.focus();
        setError("content", "내용을 입력해주세요.");
      }
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (talk.categoryList && user) {
      const isBlockUser = checkSandiTalkBlockedUser();

      if (isBlockUser) {
        // 기본 선택값을 건물톡으로 선택 해줌
        const largeCategory = talk.categoryList?.find(
          (item) => item.alias === "building"
        );

        if (largeCategory) {
          updateCategory({
            categoryInfo: { largeCategory },
          });
        }
      }
    }
  }, [talk.categoryList, user]);

  useEffect(() => {
    setSelectedMiddleCategory(
      talk?.largeCategory?.lifeCategoryList
        ?.filter(filterUnusedCategory)
        .find(
          (category) =>
            category.lifeCategoryId === talk.middleCategory?.lifeCategoryId
        ) ??
        talk?.largeCategory?.lifeCategoryList?.filter(filterUnusedCategory)[0]
    );
  }, [talk.largeCategory]);

  useEffect(() => {
    if (post) {
      setPostInfo(post);
      const matchedCategoryList = findCategory(
        talk.categoryList ?? [],
        "lifeCategoryId",
        post?.lifeCategoryId ?? -1,
        0
      );
      setSelectedMiddleCategory(matchedCategoryList?.[1]);
      setImages(
        post.fileInfo.items
          .filter((file) => file.scale === "full")
          .map((file) => file.url)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post]);

  return (
    <div className="pb-20">
      {loadingComponent}
      <StickyHeader
        title={post ? "글 수정" : "글 쓰기"}
        rightContent={
          <CommonButton
            variant={validate(true) ? "primary" : "secondary"}
            className="!py-[11px] !px-4 h-8 flex justify-center items-center whitespace-nowrap text-sm font-medium leading-[17px]"
            onClick={async () => {
              if (!validate()) return;

              setIsLoading(true);
              try {
                if (post) {
                  await handleUpdate();
                } else {
                  await handleInsert();
                }
                setPopup(post ? "completeUpdate" : "completeInsert");
              } catch (e: any) {
                if (
                  e?.response?.data?.code === -601 &&
                  e?.response?.data?.error.includes("상점 ID가 없습니다.")
                ) {
                  setErrorMessage(
                    "상점을 선택해야 메뉴 글을 작성할 수 있습니다.\n메뉴 게시판에서 상점을 먼저 선택해주세요."
                  );
                } else if (e?.response?.data?.error) {
                  setErrorMessage(e.response.data.error?.replace(". ", ".\n"));
                } else {
                  setErrorMessage(
                    (post ? "글 수정" : "글 쓰기") + "에 실패했습니다."
                  );
                }
                setPopup("error");
              } finally {
                setIsLoading(false);
              }
            }}
            disabled={isLoading}
          >
            {post ? "수정" : "등록"}
          </CommonButton>
        }
        backIcon={<TalkNewPostBackIcon />}
        onBackClick={() => {
          navigate(-1);
        }}
        className="px-5"
      />
      <div className="px-5">
        <div>
          <div className="flex flex-col py-2.5 gap-1">
            <div className=" text-brand-neutral-60 text-sm">게시판 종류</div>
          </div>
          <RadioButtonGroup
            items={radioList}
            variantActive="select"
            value={
              radioList?.find(
                (item) => item.key === talk.largeCategory?.alias
              ) ?? radioList[0]
            }
            onChange={(selectedItem) => {
              const largeCategory = talk.categoryList?.find(
                (item) => item.alias === selectedItem.key
              );

              if (largeCategory) {
                updateCategory({
                  categoryInfo: { largeCategory },
                });
              }
            }}
          />
        </div>
        <span className="text-brand-primary-magenta text-xs">
          ※{" "}
          {talk.largeCategory?.alias === "building" ? "같은 건물" : "SANDI APP"}{" "}
          이용자들 간의 소통 공간입니다.
        </span>
        <div>
          <div className="flex items-center gap-2">
            <div className="py-2.5 text-brand-neutral-60 text-sm">카테고리</div>
          </div>
          <CommonSelect
            value={getOptionFromCategory(selectedMiddleCategory).value}
            optionList={
              talk.largeCategory?.lifeCategoryList
                ?.filter(filterUnusedCategory)
                .map((category) => getOptionFromCategory(category)) ?? []
            }
            onChange={(e) => {
              const selectedCategory =
                talk.largeCategory?.lifeCategoryList?.find(
                  (category) => category.lifeCategoryId === Number(e.value)
                );
              setSelectedMiddleCategory(selectedCategory as TalkCategory);
            }}
            className="h-[50px]"
            optionOrder="between"
          />
        </div>
      </div>
      <HorizontalRule thickness={6} />

      <div className="px-5 flex flex-col gap-5">
        <TalkLabelWrapper label="제목" required>
          <CommonInput
            placeholder="제목을 입력해주세요"
            value={postInfo?.title ?? ""}
            onChange={(value) => {
              removeError("title");
              setPostInfo(
                (prev) =>
                  ({
                    ...prev,
                    title: value,
                  } as AddNewPostRequest)
              );
            }}
            error={errors["title"]}
            ref={titleRef}
          />
        </TalkLabelWrapper>
        <TalkLabelWrapper label="내용" required>
          <CommonTextArea
            resizable
            infoMessage="URL 입력 시 http://, https://, www 입력 후 빈칸 입력"
            placeholder="내용을 입력해주세요"
            value={postInfo?.content ?? ""}
            onChange={(e) => {
              removeError("content");
              setPostInfo(
                (prev) =>
                  ({
                    ...prev,
                    content: e.target.value,
                  } as AddNewPostRequest)
              );
            }}
            error={errors["content"]}
            ref={contentRef}
          />
        </TalkLabelWrapper>
        <TalkLabelWrapper label="이미지" rightLabel="최대 10장 등록 가능">
          <CommonImageInput
            maxImages={10}
            images={images}
            onChange={(images) => setImages(images)}
            thumbnailPosition="top"
            setIsLoading={setIsLoading}
            title={"게시판 이미지"}
            type="board"
          />
        </TalkLabelWrapper>
        <TalkLabelWrapper label="댓글 허용">
          <RadioButtonGroup
            value={
              useCommentOptions.find(
                (item) => item.key === postInfo.useComment?.toString()
              ) ?? useCommentOptions[0]
            }
            variantActive="select"
            items={useCommentOptions}
            onChange={(selectedItem) => {
              setPostInfo((prev) => ({
                ...prev,
                useComment: selectedItem.key === "true" ? true : false,
              }));
            }}
          />
        </TalkLabelWrapper>
      </div>

      <SimplePopup
        isOpen={popup === "completeInsert"}
        message="게시글 등록에 성공했습니다."
        onClose={() => {
          setPopup("");
          navigate("/talk");
        }}
      />
      <SimplePopup
        isOpen={popup === "completeUpdate"}
        message="게시글 수정에 성공했습니다."
        onClose={() => {
          setPopup("");
          navigate("/talk");
        }}
      />
      <SimplePopup
        isOpen={popup === "error"}
        message={errorMessage}
        onClose={() => setPopup("")}
      />
    </div>
  );
}

export type TalkLabelWrapperProps = {
  children?: React.ReactNode;
  label?: string;
  required?: boolean;
  rightLabel?: React.ReactNode;
};

export const TalkLabelWrapper = ({
  children,
  label,
  required,
  rightLabel,
}: TalkLabelWrapperProps) => {
  return (
    <div>
      <div className="flex items-center justify-between text-sm text-brand-neutral-60 py-2.5">
        <CommonLabel label={label} required={required} />
        <div className="text-brand-neutral-40">{rightLabel}</div>
      </div>
      {children}
    </div>
  );
};

export const TalkNewPostBackIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18 6L6 18"
      stroke="black"
      strokeWidth="1.75"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <path
      d="M6 6L18 18"
      stroke="black"
      strokeWidth="1.75"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
  </svg>
);
